// src/pages/LoginPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, registerUser } from '../store/authSlice';
import './LoginPage.css';
import AuthButton from '../components/auth/AuthButton';
import { useNotification } from '../components/notification/NotificationProvider';

const LoginPage = ({ onLogin }) => {
  const dispatch = useDispatch();
  const { loading, error, access_token, refresh_token } = useSelector((state) => state.auth);

  // Login form state
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  // Register form state
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerSubmitting, setRegisterSubmitting] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginSubmitting(true);
    try {
      await dispatch(loginUser({ email: loginUsername, password: loginPassword })).unwrap();
      setLoginUsername('');
      setLoginPassword('');
    } catch (err) {
      // error is handled by the slice and notification effect
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegisterSubmitting(true);
    try {
      await dispatch(registerUser({ email: registerUsername, password: registerPassword })).unwrap();
      setRegisterUsername('');
      setRegisterPassword('');
    } catch (err) {
      // error is handled by the slice and notification effect
    } finally {
      setRegisterSubmitting(false);
    }
  };

  // ✅ When access_token is updated, persist it & notify App.js
  useEffect(() => {
    if (access_token) {
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      if (onLogin) {
        onLogin(); // tell App.js to set isAuthenticated = true
      }
    }
  }, [access_token, refresh_token, onLogin]);

  const { notify } = useNotification();
  const lastErrorRef = useRef(null);

  useEffect(() => {
    if (error && lastErrorRef.current !== error) {
      notify(error, 'error', { duration: 6000 });
      lastErrorRef.current = error;
    }
    if (!error) lastErrorRef.current = null;
  }, [error, notify]);

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-grid">
        <section className="auth-panel" aria-labelledby="login-heading">
          <h3 id="login-heading">Login</h3>
          <form id="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="loginUsername">Username</label>
              <input
                type="text"
                id="loginUsername"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <div className="form-group">
              <label htmlFor="loginPassword">Password</label>
              <input
                type="password"
                id="loginPassword"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <AuthButton type="submit" disabled={loginSubmitting} form="login-form">
              {loginSubmitting ? 'Logging in...' : 'Login'}
            </AuthButton>
          </form>
        </section>

        <section className="auth-panel" aria-labelledby="register-heading">
          <h3 id="register-heading">Register</h3>
          <form id="register-form" onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="registerUsername">Username</label>
              <input
                type="text"
                id="registerUsername"
                value={registerUsername}
                onChange={(e) => setRegisterUsername(e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <div className="form-group">
              <label htmlFor="registerPassword">Password</label>
              <input
                type="password"
                id="registerPassword"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <AuthButton type="submit" disabled={registerSubmitting} form="register-form">
              {registerSubmitting ? 'Registering...' : 'Register'}
            </AuthButton>
          </form>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
